import * as React from 'react';
import type {
    CalendarPrintWeekLayoutPageProps,
    CalendarPrintWeekLayoutPageStyleProps,
    CalendarPrintWeekLayoutPageStyles,
} from './CalendarPrintWeekLayoutPage.types';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { getVisibleDateRange } from '../CalendarPrintPage/getVisibleDateRange';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import {
    addDays,
    startOfDay,
    endOfDay,
    formatWeekDayMonthDayYear,
    OwaDate,
    differenceInCalendarDays,
    getDay,
} from 'owa-datetime';
import { WeekLayout } from '..';
import { DayOfWeek } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import type { DateRange } from 'owa-datetime-utils';
import formatConflictingTimeSpan from 'owa-timeformat/lib/utils/formatConflictingTimeSpan';
import { renderIconWrapper } from 'owa-calendar-surface-dategrid/lib/surfaceItems/CalendarSurfaceItemComponentRenderer';
import { ShowAsBar, ShowAsType } from 'owa-calendar-surface';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import type { IProcessedStyleSet } from '@fluentui/style-utilities';
import {
    getCalendarEventItemSubjectText,
    getCalendarEventItemLocationText,
} from 'owa-calendar-event-attributes';
import { splitEventsIntoDays } from '../AgendaViewPage/eventFilters';

const getClassNames = classNamesFunction<
    CalendarPrintWeekLayoutPageStyleProps,
    CalendarPrintWeekLayoutPageStyles
>();

/** The base non-styled version of CalendarPrintWeekLayoutPage. */
export function CalendarPrintWeekLayoutPageBase<TEvent>(
    props: CalendarPrintWeekLayoutPageProps<TEvent>
) {
    const { styles, className } = props;
    const { events, calendarView, weekLayout } = props;

    const classNames = getClassNames(styles, {
        className: className,
    });

    const initialVisibleDateRange = getVisibleDateRange(calendarView);

    /**
     * in Horizontal and Vertical Layouts, the first day cell is the next day from the range start that is a work week day.
     * for example:
     * - if the first day in the given date-range is sunday the 1st, and the work week is mon-fri, the week layout view
     *   starts on monday the 2nd and goes through sunday the 8th
     * - if the first day in the given date-range is wednesday the 1st, and the work week is only sat + sun, the week layout view
     *   starts on saturday the 4th and goes through friday the 10th
     */
    let workweek =
        calendarView.workWeekDays && calendarView.workWeekDays.length > 0
            ? calendarView.workWeekDays
            : [DayOfWeek.Monday]; // if no work week defined, just start on Monday
    let i = 0;
    let date = startOfDay(initialVisibleDateRange.start);
    while (!workweek.includes(getDay(date)) && i <= 7) {
        date = addDays(startOfDay(initialVisibleDateRange.start), i);
        i++;
    }

    // now that we know the real start date, create the actual visible daterange
    const visibleDateRange: DateRange = {
        start: date,
        end: endOfDay(
            addDays(
                date,
                differenceInCalendarDays(initialVisibleDateRange.end, initialVisibleDateRange.start)
            )
        ),
    };

    // since the grid is broken down into cells, make a 2D array of events where first dimension is day,
    // second dimension is events in that day. Multiday items can then appear in multiple days
    const { events: visibleEventsPerDay, dates } = splitEventsIntoDays(
        (events as unknown) as CalendarEvent[],
        visibleDateRange.start,
        visibleDateRange.end,
        calendarView.view,
        workweek
    );

    const dayCount = visibleEventsPerDay.length;
    let indexMap =
        weekLayout === WeekLayout.Horizontal ? [0, 1, 2, 3, 4, 5, 6] : [0, 3, 1, 4, 2, 5, 6];

    if (dayCount <= 4 && weekLayout === WeekLayout.Vertical) {
        // due to the way the vertical layout works, the top-right cell's index changes depending on whether there are 4 or fewer total cells
        indexMap = [0, 2, 1, 3];
    }

    return (
        <div className={classNames.root}>
            <div className={classNames.row}>
                <div className={classNames.cell}>
                    {generateDayHeader(dates[indexMap[0]], classNames)}
                    {generateDailyItems(visibleEventsPerDay[indexMap[0]], classNames)}
                </div>
                {((dayCount > 1 && weekLayout === WeekLayout.Horizontal) ||
                    (dayCount > 2 && weekLayout === WeekLayout.Vertical)) && (
                    <div className={classNames.cell}>
                        {generateDayHeader(dates[indexMap[1]], classNames)}
                        {generateDailyItems(visibleEventsPerDay[indexMap[1]], classNames)}
                    </div>
                )}
            </div>
            {((dayCount > 2 && weekLayout === WeekLayout.Horizontal) ||
                (dayCount > 1 && weekLayout === WeekLayout.Vertical)) && (
                <>
                    <div className={classNames.row}>
                        <div className={classNames.cell}>
                            {generateDayHeader(dates[indexMap[2]], classNames)}
                            {generateDailyItems(visibleEventsPerDay[indexMap[2]], classNames)}
                        </div>
                        {dayCount > 2 && (
                            <div className={classNames.cell}>
                                {dayCount > 3 && ( // intentionally inside the cell div. For layout purposes, when at 3 cells, still want a blank cell in bottom right
                                    <>
                                        {generateDayHeader(dates[indexMap[3]], classNames)}
                                        {generateDailyItems(
                                            visibleEventsPerDay[indexMap[3]],
                                            classNames
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    {dayCount > 4 && (
                        <div className={classNames.row}>
                            <div className={classNames.cell}>
                                {generateDayHeader(dates[indexMap[4]], classNames)}
                                {generateDailyItems(visibleEventsPerDay[indexMap[4]], classNames)}
                            </div>
                            <div className={classNames.cell}>
                                {dayCount > 5 && (
                                    <>
                                        <div className={classNames.subcell}>
                                            {generateDayHeader(dates[indexMap[5]], classNames)}
                                            {generateDailyItems(
                                                visibleEventsPerDay[indexMap[5]],
                                                classNames
                                            )}
                                        </div>
                                        {dayCount > 6 && (
                                            <div className={classNames.subcell}>
                                                {generateDayHeader(dates[indexMap[6]], classNames)}
                                                {generateDailyItems(
                                                    visibleEventsPerDay[indexMap[6]],
                                                    classNames
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function generateDailyItems(
    events: CalendarEvent[],
    classNames: IProcessedStyleSet<CalendarPrintWeekLayoutPageStyles>
): JSX.Element[] {
    return events.map((event, index) => {
        const colorScheme = getCalendarEventColorScheme(event);
        const location = getCalendarEventItemLocationText(event);
        const organizer = event.Organizer && event.Organizer.Mailbox.Name;

        return (
            <div key={index} className={classNames.eventWrapper}>
                <div>
                    <ShowAsBar showAs={ShowAsType[event.FreeBusyType]} colorScheme={colorScheme} />
                </div>
                <div className={classNames.eventContentWrapper}>
                    <span className={classNames.mainEventContent}>
                        {formatConflictingTimeSpan(
                            event.Start.toString(),
                            event.End.toString(),
                            false /* showDate */
                        )}
                    </span>
                    <span className={classNames.mainEventContent}>
                        {renderIconWrapper(event, false)}
                    </span>
                    <span className={classNames.mainEventContent}>
                        {getCalendarEventItemSubjectText(event)}
                    </span>
                    {location && <span>{location}</span>}
                    {organizer && event.IsMeeting && <span>{organizer}</span>}
                </div>
            </div>
        );
    });
}

function generateDayHeader(
    date: OwaDate,
    classNames: IProcessedStyleSet<CalendarPrintWeekLayoutPageStyles>
): JSX.Element {
    return <div className={classNames.cellHeader}>{formatWeekDayMonthDayYear(date)}</div>;
}
